import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, SimpleChanges } from "@angular/core";
import Chart from "chart.js/auto";
import { DataStream, KpiCartesian } from "src/app/shared/models/kpi.model";
import "chartjs-adapter-moment";
import numeral from "numeral";
import { CommonModule } from "@angular/common";

@Component({
  selector: "gen-kpi-cartesian",
  templateUrl: "./gen-kpi-cartesian.component.html",
  styleUrls: ["./gen-kpi-cartesian.component.scss"],
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GenKpiCartesianComponent implements OnInit, OnDestroy {
  @Input() height: string;
  @Input() id: string = "";
  @Input() data: DataStream[];
  @Input() cartesianData: KpiCartesian;
  @Input() chartType: string = "line"; // "line" or "bar"
  @Input() timeSw: boolean = true;
  @Input() customTooltipId: string;
  @Input() maxY: number;
  @Input() displayYAxis: boolean = false;
  @Input() formatAxisAsPct: boolean = false;
  @Input() displayUnit: boolean = false;
  @Input() displayLegend: boolean = true;

  canvas: any;
  ctx: any;
  myChart: any;
  chartId: string;

  tooltipYLabel: string;
  tooltipDataPoints: any[] = [];

  profit: DataStream;
  profitPrev: DataStream;
  digitalCnt: DataStream;
  digitalPct: DataStream;
  digitalDeltaPct: DataStream;
  digitalColorCode: DataStream;
  analogCnt: DataStream;
  analogPct: DataStream;
  analogDeltaPct: DataStream;
  analogColorCode: DataStream;

  constructor(private cdr: ChangeDetectorRef) { }

  ngOnDestroy() {
    if (this.myChart) this.myChart.destroy();
  }

  ngOnInit() {
    this.chartId = "chart_" + this.id;

    if (this.id.startsWith("periodic_profit")) {
      this.profit = this.data.find((d) => d.streamCode == "CUR_PROFIT");
      this.profitPrev = this.data.find((d) => d.streamCode == "PAST_PROFIT");
    } else if (this.id.startsWith("digital_orders")) {
      this.digitalPct = this.data.find((d) => d.streamCode == "DIGITAL_PCT");
      this.digitalDeltaPct = this.data.find(
        (d) => d.streamCode == "DIGITAL_DELTA_PCT"
      );
      this.digitalColorCode = this.data.find(
        (d) => d.streamCode == "DIGITAL_COLOR_CODE"
      );
      this.analogPct = this.data.find((d) => d.streamCode == "ANALOG_PCT");
      this.analogDeltaPct = this.data.find(
        (d) => d.streamCode == "ANALOG_DELTA_PCT"
      );
      this.analogColorCode = this.data.find(
        (d) => d.streamCode == "ANALOG_COLOR_CODE"
      );
      this.analogCnt = this.data.find((d) => d.streamCode == "ANALOG_CNT");
      this.digitalCnt = this.data.find((d) => d.streamCode == "DIGITAL_CNT");
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.drawChart();
  }

  ngAfterViewInit() {
    this.drawChart();
  }

  getDataSets(): Array<any> {
    // If cartesianData is provided, use it instead of data array
    if (this.cartesianData) {
      return this.getDataSetsFromCartesian();
    }

    let datasets = [];
    this.data
      .filter((d) => d.streamCode != "LABELS" && d.visible)
      .forEach((s) => {
        let dataset = {
          label: s.descr,
          data: this.getDataFromStream(s),
          fill: s.fill,
          borderColor: s.color,
          borderWidth: 2,
          backgroundColor: s.fillColor ?? s.color,
          type: s.type,
          code: s.streamCode,
          borderDash: s.dashed ? [5, 5] : [],
          pointRadius: s.streamCode == "BREAK_EVEN" ? 0 : s.dashed ? 4 : 4,
          pointStyle: s.dashed ? "circle" : "circle",
          yAxisID: s.yAxisID,
          values2: s.values2,
          unitDescr: s.unitDescr,
          hidden: s.hidden,
        };
        datasets.push(dataset);
      });
    return datasets;
  }

  getDataSetsFromCartesian(): Array<any> {
    if (!this.cartesianData || !this.cartesianData.datapointDescr || this.cartesianData.datapointDescr.length === 0) return [];

    let data = [];
    for (let i = 0; i < this.cartesianData.datapointDescr.length; i++) {
      data.push({
        x: this.cartesianData.datapointDescr[i],
        y: this.cartesianData.valCnt[i] || 0,
      });
    }

    return [{
      label: this.cartesianData.display,
      data: data,
      backgroundColor: "rgba(0, 148, 221, 0.8)",
      borderColor: "rgba(0, 148, 221, 1)",
      borderWidth: 1,
      barThickness: 'flex',
      maxBarThickness: 30,
      borderRadius: {
        topLeft: 4,
        topRight: 4,
        bottomLeft: 0,
        bottomRight: 0,
      },
      yAxisID: "main",
    }];
  }

  getDataFromStream(stream: DataStream): any[] {
    let data = [];
    for (let i = 0; i < stream.labels.length; i++) {
      data.push({
        x: stream.labels[i],
        y: stream.values[i],
        unitDescr: stream.unitDescr?.length > 0 ? stream.unitDescr[i] : "",
      });
    }
    return data;
  }

  drawChart() {
    if (this.myChart) this.myChart.destroy();

    this.canvas = document.getElementById(this.chartId);
    if (!this.canvas) return;

    // Don't draw if no data
    if (this.cartesianData && (!this.cartesianData.datapointDescr || this.cartesianData.datapointDescr.length === 0)) return;
    if (!this.cartesianData && (!this.data || this.data.length === 0)) return;

    this.ctx = this.canvas.getContext("2d");
    Chart.defaults.font = {
      family: "Rubik",
      size: 14,
    };
    const chartType = (this.chartType || "line") as "line" | "bar";
    const labels = this.cartesianData
      ? this.cartesianData.datapointDescr
      : (this.timeSw
        ? null
        : this.data?.filter((d) => d.streamCode == "LABELS")[0]?.labels);

    this.myChart = new Chart(this.ctx, {
      type: chartType,
      data: {
        labels: labels,
        datasets: this.getDataSets(),
      },
      options: {
        responsive: true,

        maintainAspectRatio: false,
        animation: {
          duration: 0,
        },
        layout: {
          padding: {
            top: 0,
            bottom: this.chartType === "bar" ? 10 : 0,
            left: this.displayYAxis ? 15 : 0,
            right: this.chartType === "bar" ? 10 : 0,
          },
        },
        scales: {
          y: {
            display: false,
          },
          x: {
            type: this.cartesianData ? "category" : (this.timeSw ? "time" : "category"),
            stacked: false,
            ticks: {
              maxTicksLimit: this.cartesianData ? undefined : (this.id.startsWith("cumul_income") ? 8 : 12),
            },
            time: {
              unit: "day",
              displayFormats: {
                day: "DD",
              },
              // stepSize: 4,
              tooltipFormat: "DD",
            },
            offset: true,
            display: true,
            title: {
              display: false,
            },
          },
          main: {
            //suggestedMin: 0,
            max: this.maxY,
            stacked: false,

            display: this.displayYAxis,
            position: "left",
            //min: 0,
            type: "linear",
            grid: {
              display: false,
            },
            ticks: {
              display: this.displayYAxis,
              maxTicksLimit: 10,
              ...(this.formatAxisAsPct ? {} : { precision: 0 }),
              callback: this.formatAxisAsPct
                ? (value) => {
                  return value + "%";
                }
                : (value) => {
                  return numeral(value).format("0,0");
                },
            },
            beginAtZero: true,
          },
        },
        plugins: {
          legend: {
            display: this.displayLegend,
            position: "top",
            align: "center",
            rtl: true,
            labels: {
              usePointStyle: true,
              boxWidth: 8,
              boxHeight: 8,
              font: {
                size: 12,
              },
              // boxPadding: 5,
              padding: 15,
            },
          },
          tooltip: {
            enabled: !this.customTooltipId,
            position: "nearest",
            external: this.customTooltipId ? this.externalTooltipHandler : null,
            usePointStyle: true,
            displayColors: false,
            backgroundColor: "rgb(235, 238, 242)",
            bodyColor: "black",
            titleColor: "black",
            rtl: true,
            padding: 6,
          },
        },
      },
    });
  }

  getOrCreateTooltip = (chart) => {
    let tooltipEl = chart.canvas.parentNode.querySelector("div");

    if (!tooltipEl) {
      tooltipEl = document.getElementById(this.customTooltipId);
      tooltipEl.style.opacity = 1;
      tooltipEl.style.pointerEvents = "none";
      tooltipEl.style.position = "absolute";
      tooltipEl.style.transform = "translate(-50%, 0)";
      tooltipEl.style.transition = "all .1s ease";
      chart.canvas.parentNode.appendChild(tooltipEl);
    }

    return tooltipEl;
  };

  externalTooltipHandler = (context) => {
    // Tooltip Element
    const { chart, tooltip } = context;
    const tooltipEl = this.getOrCreateTooltip(chart);

    // Hide if no tooltip
    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = 0;
      return;
    }
    var tooltipModel = context.tooltip;

    this.tooltipYLabel = tooltipModel.title[0];
    this.tooltipDataPoints = tooltip.dataPoints;

    const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

    // Display, position, and set styles for font
    tooltipEl.style.opacity = 1;
    tooltipEl.style.left = positionX + tooltip.caretX + "px";
    tooltipEl.style.top = positionY + tooltip.caretY + 15 + "px";
    tooltipEl.style.font = tooltip.options.bodyFont.string;
    tooltipEl.style.padding =
      tooltip.options.padding + "px " + tooltip.options.padding + "px";
    this.cdr.markForCheck();
  };

  getCurDateLabel(dd: string) {
    return (
      dd + "/" + (new Date().getMonth() + 1) + "/" + new Date().getFullYear()
    );
  }
}
